import React, { JSX, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { I } from "services/lang/LanguageService";
import { Tooltip } from "primereact/tooltip";
import { toInstant } from "utils/time-utils";
import { Duration, Instant } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import DatasetButton from "./DatasetButton";
import "./UniversalDatasetTable.scss";
import {
  CellType,
  ICellConfig,
  IUniversalDatasetTableColumn,
  UniversalDatasetTableRowProps,
} from "./UniversalDatasetTableTypes";
import { findUniversalTableRowIdentifiers } from "./universal-dataset-table-utils";

const UniversalDatasetTableRow = ({
  recentData,
  columnsOptions,
  compactMode = false,
  rowData,
  maxAge,
  isSelected,
  rowIndex,
  onClick,
}: UniversalDatasetTableRowProps) => {
  const identifiers = findUniversalTableRowIdentifiers(rowData);
  const currentRowData = recentData?.filter(ds => identifiers.includes(ds.identifier));
  const [expiredColumns, setExpiredColumns] = useState<Record<number, boolean>>({});

  const renderCellContent = (
    col: IUniversalDatasetTableColumn,
    index: number,
    dataset?: DatasetDataInfoResponse,
    isExpired?: boolean,
    onExpiredChange?: (isExpired: boolean) => void
  ) => {
    if (col.cell.type === CellType.STRING || col.cell.type === CellType.NUMBER || col.cell.type === CellType.UNIT) {
      return (
        <div>
          {col.cell.type === CellType.STRING && typeof col.cell.value === "string" ? I(col.cell.value) : col.cell.value}
        </div>
      );
    }

    if (col.cell.type === CellType.DATASET) {
      if (dataset) {
        return (
          <DatasetButton
            dataset={dataset}
            columnIndex={index}
            rowData={rowData}
            compactMode={compactMode}
            maxAge={maxAge}
            isExpired={isExpired}
            onExpiredChange={onExpiredChange}
          />
        );
      }
    }

    if (col.cell.type === CellType.URL) {
      const tooltipId = `url-icon-${uuidv4()}`;
      return (
        <>
          <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} />
          <i
            id={tooltipId}
            className="pi pi-link cursor-pointer"
            onClick={() => window.open(col.cell.value?.toString())}
            data-pr-tooltip={col.cell.value?.toString()}
          />
        </>
      );
    }

    return null;
  };

  const getColor = (
    minValue?: number,
    maxValue?: number,
    minColor?: string,
    maxColor?: string,
    currentValue?: number
  ): string | undefined => {
    if (currentValue === undefined) return undefined;

    if (minValue !== undefined && minColor && currentValue <= minValue) {
      return minColor;
    }

    if (maxValue !== undefined && maxColor && currentValue >= maxValue) {
      return maxColor;
    }

    return undefined;
  };

  const getTextColor = (cell: ICellConfig, currentValue?: number): string | undefined => {
    if (!cell.setMinMaxColors) return cell.highlightColor?.color;

    const minValue = cell.minMaxValue?.minValue;
    const maxValue = cell.minMaxValue?.maxValue;
    const minColor = cell.minMaxColor?.minTextColor;
    const maxColor = cell.minMaxColor?.maxTextColor;

    const color = getColor(minValue, maxValue, minColor, maxColor, currentValue);
    return color ?? cell.highlightColor?.color;
  };

  const getBgColor = (cell: ICellConfig, currentValue?: number): string | undefined => {
    if (!cell.setMinMaxColors) return cell.highlightColor?.bgColor;

    const minValue = cell.minMaxValue?.minValue;
    const maxValue = cell.minMaxValue?.maxValue;
    const minColor = cell.minMaxColor?.minBgColor;
    const maxColor = cell.minMaxColor?.maxBgColor;

    const color = getColor(minValue, maxValue, minColor, maxColor, currentValue);
    return color ?? cell.highlightColor?.bgColor;
  };

  const handleRowClick = (el: EventTarget) => {
    if (!(el instanceof HTMLElement)) return;
    onClick?.(rowIndex);
  };

  const isDatasetExpired = (dataset: DatasetDataInfoResponse | undefined, currentMaxAge?: number): boolean => {
    if (!dataset || !currentMaxAge) return false;
    const firstKey = dataset.data?.[0]?.key;
    if (!firstKey) return false;
    const datasetTime = toInstant(firstKey);
    const ageSeconds = Duration.between(datasetTime, Instant.now()).seconds();

    return ageSeconds > currentMaxAge;
  };

  const renderSeparatorRow = () => (
    <td colSpan={columnsOptions.length + 1}>
      <div style={{ height: "15px" }} />
    </td>
  );

  const renderContentRow = () =>
    rowData.columns.map((col: IUniversalDatasetTableColumn, index: number) => {
      if (!columnsOptions[index]?.columnVisibility) return null;
      const dataset = currentRowData?.find(ds => ds.identifier === col.cell.value);
      const currentValue = dataset?.data?.[0]?.value;
      const isExpired =
        expiredColumns[index] ?? (col.cell.type === CellType.DATASET ? isDatasetExpired(dataset, maxAge) : false);

      const handleExpiredChange = (expired: boolean) => {
        setExpiredColumns(prev => {
          if (prev[index] === expired) return prev;
          return { ...prev, [index]: expired };
        });
      };

      const props: JSX.IntrinsicElements["td"] = {};

      if (!isExpired) {
        props.style = {
          color: getTextColor(col.cell, currentValue),
          backgroundColor: getBgColor(col.cell, currentValue),
        };
      }

      return (
        <td key={uuidv4()} {...props}>
          {renderCellContent(col, index, dataset, isExpired, handleExpiredChange)}
        </td>
      );
    });

  return (
    <tr onClick={e => handleRowClick(e.target)} className={isSelected ? "selected-row" : "not-selected-row"}>
      {rowData.isSeparator ? renderSeparatorRow() : renderContentRow()}
    </tr>
  );
};

function areEqual(prevProps: UniversalDatasetTableRowProps, nextProps: UniversalDatasetTableRowProps) {
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.rowData === nextProps.rowData &&
    prevProps.recentData === nextProps.recentData &&
    prevProps.columnsOptions === nextProps.columnsOptions &&
    prevProps.compactMode === nextProps.compactMode &&
    prevProps.maxAge === nextProps.maxAge
  );
}

export default React.memo(UniversalDatasetTableRow, areEqual);
